import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowRight, FolderPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const GOALS = [
  { id: "build_audience", label: "Build an audience" },
  { id: "generate_leads", label: "Generate leads" },
  { id: "sell_products", label: "Sell a digital product" },
  { id: "promote_services", label: "Promote a service" },
  { id: "promote_affiliate", label: "Promote affiliate offers" },
  { id: "educate", label: "Teach / educate" },
];

export default function NewProject() {
  const navigate = useNavigate();
  const location = useLocation();
  const { setHasProfile, refreshProfile, refreshActiveProject } = useAuth();
  const isOnboarding = location.pathname === "/onboarding";
  const [name, setName] = useState("");
  const [goal, setGoal] = useState("build_audience");
  const [saving, setSaving] = useState(false);

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/projects", { name, goal });
      setHasProfile(true);
      await Promise.all([refreshProfile(), refreshActiveProject()]);
      toast.success("Project created — now set up your offer");
      navigate(`/offer-profiles/${data.offer_profile_id}`);
    } catch (e) {
      toast.error("Could not create project");
      setSaving(false);
    }
  };

  return (
    <div className={isOnboarding ? "flex min-h-screen items-center justify-center bg-background px-4 py-10" : ""}>
      <div className="w-full max-w-xl fade-up" data-testid="new-project-page">
        {isOnboarding && (
          <div className="mb-6 flex items-center gap-2.5">
            <img src="/logo-mark.png" alt="Hook2Income AI" className="h-9 w-9 object-contain" />
            <span className="font-head text-lg font-bold">Hook2Income<span style={{ color: "hsl(var(--gold))" }}> AI</span></span>
          </div>
        )}
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
            <FolderPlus size={24} />
          </div>
          <div>
            <h1 className="font-head text-4xl font-bold">{isOnboarding ? "Create your first project" : "New project"}</h1>
            <p className="text-muted-foreground">A project is one goal — its own offer, campaigns & Income Mode.</p>
          </div>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="text-sm font-semibold">Project name</label>
            <Input autoFocus data-testid="project-name" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Side Income Generator, Course on Anxiety" className="text-base" />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-semibold">What's the goal?</label>
            <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3">
              {GOALS.map((g) => (
                <button key={g.id} onClick={() => setGoal(g.id)} data-testid={`project-goal-${g.id}`}
                  className={`rounded-xl border p-3 text-sm font-semibold transition-colors ${
                    goal === g.id ? "border-primary bg-primary/5 ring-2 ring-primary" : "border-border hover:border-primary/40"
                  }`}>{g.label}</button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex justify-end">
          <Button onClick={create} disabled={!name.trim() || saving} data-testid="create-project-btn"
            className="rounded-full bg-primary px-6 hover:bg-primary/90">
            {saving ? <Loader2 size={18} className="mr-1.5 animate-spin" /> : null}
            Set up the offer <ArrowRight size={18} className="ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
